// evm-utils.ts — the EVM twin of sol/sol-utils.ts's genSolanaMessage. It builds
// the canonical EIP-4361 (Sign-In with Ethereum, SIWE) prompt the bounded-auth
// issuer verifies, and normalizes EVM addresses to the single canonical form the
// identity layer matches on.
//
// The SIWE message MUST match bounded-auth's parser byte-for-byte
// (packages/cdk/cloudflare/bounded-auth/src/verify-siwe.ts → parseSiweMessage):
//   <domain> wants you to sign in with your Ethereum account:
//   <address>
//   <blank>
//   <statement>
//   <blank>
//   URI: <uri>
//   Version: 1
//   Chain ID: <chainId>
//   Nonce: <nonce>
//   Issued At: <ISO8601>
//   Expiration Time: <ISO8601>
//   Resources:
//   - bounded:app:<appId>
//
// Bindings the verifier enforces (so we produce them exactly): the address is
// lowercased (identity match is case-sensitive, so lowercase-at-boundary is
// mandatory), the single `bounded:app:<appId>` resource binds the signature to
// THIS app (a signature for one app must not mint a session for another — the
// SIWE analog of genSolanaMessage's `App ID:` line), and Expiration Time is
// REQUIRED and must fall within the issuer's 10-minute max validity window.
/** Default statement — kept identical to genSolanaMessage's for a consistent prompt. */
const DEFAULT_STATEMENT = 'Sign this message to authenticate with our application.';
/** Match bounded-auth's MAX_AUTH_WINDOW_MS (10 min); we default to a 5-min window. */
const DEFAULT_VALIDITY_MS = 5 * 60 * 1000;
/**
 * Build the canonical EIP-4361 SIWE message the bounded-auth issuer verifies.
 *
 * @param address  the connected EVM account (lowercased into the prompt).
 * @param nonce    single-use nonce from genAuthNonce().
 * @param appId    the target app id (bound as the sole `bounded:app:<appId>` resource).
 * @param domain   RFC-3986 authority of the requesting site (e.g. window.location.host).
 * @param uri      the requesting URI; its origin is bound to the domain and actual browser Origin.
 */
export function genSiweMessage(address, nonce, appId, domain, uri, options = {}) {
    var _a, _b, _c, _d, _e;
    const addr = normalizeEvmAddress(address);
    if (!addr)
        throw new Error('SIWE address must be a 20-byte 0x-prefixed EVM address');
    const statement = (_a = options.statement) !== null && _a !== void 0 ? _a : DEFAULT_STATEMENT;
    const version = (_b = options.version) !== null && _b !== void 0 ? _b : '1';
    const chainId = (_c = options.chainId) !== null && _c !== void 0 ? _c : 1;
    if (statement !== DEFAULT_STATEMENT)
        throw new Error('SIWE statement must use the canonical Bounded authentication text');
    if (version !== '1')
        throw new Error('SIWE version must be 1');
    if (!Number.isSafeInteger(chainId) || chainId <= 0)
        throw new Error('SIWE chainId must be a positive safe integer');
    if (!/^[A-Za-z0-9]{8,}$/.test(nonce))
        throw new Error('SIWE nonce must be at least 8 alphanumeric characters');
    if (!appId || /[\r\n]/.test(appId))
        throw new Error('SIWE appId is required and must be single-line');
    if (!domain || domain !== domain.trim() || /[\s/@?#\\]/.test(domain))
        throw new Error('SIWE domain must be an RFC-3986 authority');
    try {
        const parsedAuthority = new URL(`https://${domain}`);
        if (parsedAuthority.username || parsedAuthority.password || parsedAuthority.pathname !== '/' ||
            parsedAuthority.search || parsedAuthority.hash || parsedAuthority.host.toLowerCase() !== domain) {
            throw new Error('non-canonical authority');
        }
    }
    catch (_f) {
        throw new Error('SIWE domain must be a canonical lowercase RFC-3986 authority');
    }
    let parsedUri;
    try {
        parsedUri = new URL(uri);
    }
    catch (_g) {
        throw new Error('SIWE URI must be an absolute URL');
    }
    const local = parsedUri.hostname === 'localhost' || parsedUri.hostname === '127.0.0.1' || parsedUri.hostname === '[::1]';
    if (parsedUri.protocol !== 'https:' && !(local && parsedUri.protocol === 'http:')) {
        throw new Error('SIWE URI must use HTTPS (HTTP is allowed only for localhost)');
    }
    if (parsedUri.username || parsedUri.password || parsedUri.hash || parsedUri.host.toLowerCase() !== domain.toLowerCase()) {
        throw new Error('SIWE URI authority must match the SIWE domain');
    }
    const issuedAtDate = (_d = options.issuedAt) !== null && _d !== void 0 ? _d : new Date();
    const expirationDate = (_e = options.expirationTime) !== null && _e !== void 0 ? _e : new Date(issuedAtDate.getTime() + DEFAULT_VALIDITY_MS);
    if (!Number.isFinite(issuedAtDate.getTime()) || !Number.isFinite(expirationDate.getTime()) || expirationDate <= issuedAtDate) {
        throw new Error('SIWE validity window is invalid');
    }
    if (expirationDate.getTime() - issuedAtDate.getTime() > 10 * 60 * 1000) {
        throw new Error('SIWE validity window exceeds 10 minutes');
    }
    const issuedAt = issuedAtDate.toISOString();
    const expirationTime = expirationDate.toISOString();
    return (`${domain} wants you to sign in with your Ethereum account:\n${addr}\n\n` +
        `${statement}\n\n` +
        `URI: ${parsedUri.toString()}\n` +
        `Version: ${version}\n` +
        `Chain ID: ${chainId}\n` +
        `Nonce: ${nonce}\n` +
        `Issued At: ${issuedAt}\n` +
        `Expiration Time: ${expirationTime}\n` +
        `Resources:\n` +
        `- bounded:app:${appId}`);
}
/**
 * Lowercase an EVM address to the canonical identity form. EVM addresses are
 * case-insensitive on-chain but the identity layer (@user.evmAddress) matches
 * case-sensitively, so every boundary normalizes to lowercase. Does not validate
 * shape — use normalizeEvmAddress() when the input is untrusted.
 */
export function toLowerEvmAddress(address) {
    return address.trim().toLowerCase();
}
/**
 * Normalize an untrusted EVM address to canonical lowercase 0x-hex, or null when
 * it isn't a well-formed 20-byte hex address. Mirrors the realtime-worker's
 * normalizeEvmAddress (auth.ts) EXACTLY so a client-derived @user.evmAddress and
 * the server-authenticated one are byte-identical (fail-closed on malformed).
 */
export function normalizeEvmAddress(raw) {
    if (typeof raw !== 'string')
        return null;
    const v = raw.trim().toLowerCase();
    return /^0x[0-9a-f]{40}$/.test(v) ? v : null;
}
